import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Edit3, Trash2 } from 'lucide-react';
import { Modal, ConfirmationModal } from './Helpers';

const ApartmentManagementView = ({ showToast, currentUser }) => {
  const [apartments, setApartments] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [blocks, setBlocks] = useState([]); // Tüm bloklar
  const [filteredBlocksForModal, setFilteredBlocksForModal] = useState([]);
  const [people, setPeople] = useState([]);

  const [selectedBlockIdInTableFilter, setSelectedBlockIdInTableFilter] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingApartment, setEditingApartment] = useState(null);
  const [modalSelectedBuildingId, setModalSelectedBuildingId] = useState('');
  const [modalSelectedBlockId, setModalSelectedBlockId] = useState('');
  const [apartmentNumber, setApartmentNumber] = useState('');
  const [apartmentFloor, setApartmentFloor] = useState('');
  const [arsaPayi, setArsaPayi] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [residentId, setResidentId] = useState('');
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, apartmentId: null });

  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoadingApartments, setIsLoadingApartments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 1. Temel verileri (binalar, tüm bloklar, kişiler) yükle
  const loadInitialData = useCallback(async () => {
    console.log("ApartmentManagementView: Mount - Fetching initial data (buildings, all blocks, people).");
    setIsLoadingInitialData(true);
    try {
      const [buildingsData, allBlocksData, peopleData] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/buildings`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }).then(res => res.json()),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blocks`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }).then(res => res.json()),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/people`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }).then(res => res.json()),
      ]);
      console.log("ApartmentManagementView: Initial data RECEIVED.");
      setBuildings(buildingsData || []);
      setBlocks(allBlocksData || []);
      setPeople(peopleData || []);
    } catch (error) {
      showToast("Başlangıç verileri yüklenirken hata: " + error.message, "error");
    } finally {
      setIsLoadingInitialData(false);
    }
  }, [showToast]);

  // Component ilk yüklendiğinde başlangıç verilerini çek
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // 2. Daireleri filtreye göre yükle
  const loadApartments = useCallback(async (blockFilter) => {
    console.log("ApartmentManagementView: loadApartments called with filters:", { blockFilter });
    setIsLoadingApartments(true);
    try {
      const filters = {};
      // Always use the current user's buildingId for filtering
      if (currentUser && currentUser.buildingId) {
        filters.buildingId = currentUser.buildingId;
      }
      if (blockFilter) filters.blockId = blockFilter;

      console.log("ApartmentManagementView: Fetching apartments with effective filters:", filters);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/apartments?` + new URLSearchParams(filters), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.reload();
          throw new Error('Oturum süresi doldu veya yetkiniz yok.');
        }
        throw new Error('Daireler yüklenemedi.');
      }
      const data = await response.json();
      console.log("ApartmentManagementView: Apartments RECEIVED.", data);
      setApartments(data || []);
    } catch (error) {
      showToast("Daireler yüklenirken hata: " + error.message, "error");
      setApartments([]);
    } finally {
      setIsLoadingApartments(false);
    }
  }, [showToast, currentUser]); // Add currentUser to dependencies

  // Tablo filtreleri değiştiğinde VEYA başlangıç verileri yüklendikten sonra daireleri yükle
  useEffect(() => {
    if (isLoadingInitialData) {
      console.log("ApartmentManagementView: (Apartment Loader) Waiting for initial data before loading apartments.");
      return;
    }
    console.log("ApartmentManagementView: (Apartment Loader) Initial data loaded. Triggering apartment load with filters:", {selectedBlockIdInTableFilter});
    loadApartments(selectedBlockIdInTableFilter);
  }, [selectedBlockIdInTableFilter, isLoadingInitialData, loadApartments]);

  // 3. Modal içindeki bina seçimi değiştiğinde blokları filtrele
  useEffect(() => {
    console.log("ApartmentManagementView: Modal building selection effect. ModalBuildingID:", modalSelectedBuildingId, "Total Blocks:", blocks.length);
    console.log("ApartmentManagementView: All Blocks:", blocks);
    if (modalSelectedBuildingId && blocks.length > 0) {
      const filtered = blocks.filter(b => {
        console.log(`ApartmentManagementView: Comparing Block ID ${b.id} Building ID ${b.buildingId} with Modal Building ID ${modalSelectedBuildingId}`);
        return b.buildingId === parseInt(modalSelectedBuildingId); // Ensure type consistency
      });
      console.log("ApartmentManagementView: Filtered Blocks for Modal:", filtered);
      setFilteredBlocksForModal(filtered);
      if (!editingApartment || (editingApartment && editingApartment.buildingId !== parseInt(modalSelectedBuildingId))) {
        setModalSelectedBlockId('');
      }
    } else {
      console.log("ApartmentManagementView: No modal building selected or no blocks available.");
      setFilteredBlocksForModal([]);
      if (!editingApartment) {
        setModalSelectedBlockId('');
      }
    }
  }, [modalSelectedBuildingId, blocks, editingApartment]);

  // refreshApartments fonksiyonu
  const refreshApartments = useCallback(async () => {
    console.log("ApartmentManagementView: refreshApartments called.");
    loadApartments(selectedBlockIdInTableFilter);
  }, [selectedBlockIdInTableFilter, loadApartments]);

  const handleAddOrUpdateApartment = async (e) => {
    e.preventDefault();
    if (!modalSelectedBuildingId || !modalSelectedBlockId || !apartmentNumber.trim() || !arsaPayi.trim()) {
      showToast('Bina, Blok, Daire No ve Arsa Payı alanları zorunludur.', 'error');
      return;
    }
    const arsaPayiFloat = parseFloat(arsaPayi);
    if (isNaN(arsaPayiFloat) || arsaPayiFloat <= 0) {
      showToast('Arsa Payı geçerli pozitif bir sayı olmalıdır.', 'error');
      return;
    }

    const apartmentData = { 
      buildingId: parseInt(modalSelectedBuildingId), 
      blockId: parseInt(modalSelectedBlockId),
      apartmentNumber: apartmentNumber.trim(), 
      floor: apartmentFloor.trim(),
      arsaPayi: arsaPayiFloat, 
      ownerId: ownerId ? parseInt(ownerId) : null, 
      residentId: residentId ? parseInt(residentId) : null,
    };
    setIsSubmitting(true);
    try {
      if (editingApartment) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/apartments/${editingApartment.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apartmentData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Daire güncellenemedi.');
        }
        showToast('Daire başarıyla güncellendi.', 'success');
      } else {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/apartments`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apartmentData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Daire eklenemedi.');
        }
        showToast('Daire başarıyla eklendi.', 'success');
      }
      refreshApartments();
      closeApartmentModal();
    } catch (error) {
      showToast('Daire kaydedilirken hata: ' + (error.message || "Bilinmeyen bir hata oluştu."), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteApartment = async (apartmentId) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/apartments/${apartmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Daire silinemedi.');
      }
      showToast('Daire başarıyla silindi.', 'success');
      refreshApartments();
    } catch (error) {
      showToast('Daire silinirken hata: ' + (error.message || "Bilinmeyen bir hata oluştu."), 'error');
    } finally {
      setIsSubmitting(false);
      setConfirmDeleteModal({ isOpen: false, apartmentId: null });
    }
  };

  const openApartmentModalForEdit = (apartment) => { 
    setEditingApartment(apartment); 
    setModalSelectedBuildingId(apartment.buildingId.toString()); 
    setModalSelectedBlockId(apartment.blockId.toString()); 
    setApartmentNumber(apartment.apartmentNumber); 
    setApartmentFloor(apartment.floor || ''); 
    setArsaPayi(apartment.arsaPayi.toString()); 
    setOwnerId(apartment.ownerId ? apartment.ownerId.toString() : ''); 
    setResidentId(apartment.residentId ? apartment.residentId.toString() : ''); 
    setIsModalOpen(true);
  };

  const openApartmentModalForNew = () => {
    // A user is always associated with one building, so we can directly use it.
    if (!currentUser || !currentUser.buildingId) {
      showToast("Kullanıcının atandığı bir bina bulunamadı. Lütfen yöneticinizle iletişime geçin.", "error");
      return;
    }
    setEditingApartment(null);
    setModalSelectedBuildingId(currentUser.buildingId.toString());
    setModalSelectedBlockId('');
    setApartmentNumber('');
    setApartmentFloor('');
    setArsaPayi('');
    setOwnerId('');
    setResidentId('');
    setIsModalOpen(true);
  };

  const closeApartmentModal = () => { 
    setIsModalOpen(false); 
    setEditingApartment(null); 
    setModalSelectedBuildingId(''); 
    setModalSelectedBlockId(''); 
    setApartmentNumber(''); 
    setApartmentFloor(''); 
    setArsaPayi(''); 
    setOwnerId(''); 
    setResidentId('');
  };

  const getBuildingName = (bId) => buildings.find(b => b.id === bId)?.name || '';
  const getBlockName = (blId) => blocks.find(bl => bl.id === blId)?.name || '';
  const getPersonName = (pId) => people.find(p => p.id === pId)?.name || '';

  // Filter blocks for the table based on the current user's building
  const blocksForTableFilter = currentUser && currentUser.buildingId && blocks.length > 0
    ? blocks.filter(block => String(block.buildingId) === String(currentUser.buildingId))
    : [];

  // Tarih formatlama fonksiyonu (Invalid Date sorununu çözmek için)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  };

  if (isLoadingInitialData) {
    return (
      <div className="bg-white p-6 rounded-lg shadow flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Temel veriler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow relative">
      {(isLoadingApartments || isSubmitting) && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-blue-700">Yükleniyor / İşlem yapılıyor...</p>
        </div>
      )}
      <ConfirmationModal 
        isOpen={confirmDeleteModal.isOpen} 
        onClose={() => setConfirmDeleteModal({ isOpen: false, apartmentId: null })} 
        title="Daireyi Sil" 
        message="Bu daireyi silmek istediğinizden emin misiniz? Bu daireye ait aidat kayıtları varsa önce onlar silinmelidir." 
        onConfirm={() => handleDeleteApartment(confirmDeleteModal.apartmentId)} 
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Daire Yönetimi</h1>
        <button 
          onClick={openApartmentModalForNew} 
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center disabled:opacity-50" 
          disabled={buildings.length === 0 || isLoadingInitialData || isLoadingApartments || isSubmitting}
        >
          <PlusCircle size={18} className="mr-2" /> Yeni Daire Ekle
        </button>
      </div>
      {/* Bina filtresi kaldırıldı, çünkü kullanıcı tek bir binadan sorumlu */}
      <div className="grid md:grid-cols-1 gap-4 mb-6 p-4 border rounded-lg bg-gray-50">
        <div>
          <label htmlFor="blockTableFilterApt" className="block text-sm font-medium text-gray-700 mb-1">Bloğa Göre Filtrele:</label>
          <select
            id="blockTableFilterApt"
            value={selectedBlockIdInTableFilter}
            onChange={(e) => setSelectedBlockIdInTableFilter(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled={blocksForTableFilter.length === 0 || isLoadingInitialData || isLoadingApartments || isSubmitting}
          >
            <option value="">Tüm Bloklar</option>
            {blocksForTableFilter.map(bl => <option key={bl.id} value={bl.id}>{bl.name}</option>)}
          </select>
        </div>
      </div>
      <Modal isOpen={isModalOpen} onClose={closeApartmentModal} title={editingApartment ? "Daireyi Düzenle" : "Yeni Daire Ekle"} size="xl">
        <form onSubmit={handleAddOrUpdateApartment}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {/* Bina seçimi artık sabit, kullanıcıya gösterilmiyor */}
            <div className="hidden"> {/* Gizli alan */}
              <label htmlFor="modalBuildingSelectApt" className="label-form-style">Bina:</label>
              <input
                type="hidden"
                id="modalBuildingSelectApt"
                value={modalSelectedBuildingId}
                readOnly
              />
            </div>
            <div>
              <label htmlFor="modalBlockSelectApt" className="label-form-style">Blok:</label>
              <select
                id="modalBlockSelectApt"
                value={modalSelectedBlockId}
                onChange={(e) => setModalSelectedBlockId(e.target.value)}
                className="input-form-style"
                required
                disabled={filteredBlocksForModal.length === 0}
              >
                <option value="">Blok Seçin</option>
                {filteredBlocksForModal.map(bl => <option key={bl.id} value={bl.id}>{bl.name}</option>)}
              </select>
              {modalSelectedBuildingId && filteredBlocksForModal.length === 0 && blocks.length > 0 && !isLoadingInitialData && <p className="text-xs text-red-500 mt-1">Bu binaya ait blok bulunamadı. Lütfen önce blok ekleyin.</p>}
            </div>
            <div>
              <label htmlFor="modalAptNoApt" className="label-form-style">Daire No:</label>
              <input
                type="text"
                id="modalAptNoApt"
                value={apartmentNumber}
                onChange={(e) => setApartmentNumber(e.target.value)}
                className="input-form-style"
                required
              />
            </div>
            <div>
              <label htmlFor="modalAptFloorApt" className="label-form-style">Kat:</label>
              <input 
                type="text" 
                id="modalAptFloorApt" 
                value={apartmentFloor} 
                onChange={(e) => setApartmentFloor(e.target.value)} 
                className="input-form-style" 
              />
            </div>
            <div>
              <label htmlFor="modalArsaPayiApt" className="label-form-style">Arsa Payı:</label>
              <input 
                type="number" 
                step="any" 
                id="modalArsaPayiApt" 
                value={arsaPayi} 
                onChange={(e) => setArsaPayi(e.target.value)} 
                className="input-form-style" 
                required 
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="modalOwnerApt" className="label-form-style">Kat Maliki:</label>
              <select 
                id="modalOwnerApt" 
                value={ownerId} 
                onChange={(e) => setOwnerId(e.target.value)} 
                className="input-form-style"
              >
                <option value="">Kat Maliki Seçin</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="modalResidentApt" className="label-form-style">Sakin:</label>
              <select 
                id="modalResidentApt" 
                value={residentId} 
                onChange={(e) => setResidentId(e.target.value)} 
                className="input-form-style"
              >
                <option value="">Sakin Seçin</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button 
              type="button" 
              onClick={closeApartmentModal} 
              className="btn-secondary-style" 
              disabled={isSubmitting}
            >
              İptal
            </button>
            <button 
              type="submit" 
              className="btn-primary-style flex items-center disabled:opacity-50" 
              disabled={isSubmitting}
            >
              {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>}
              {editingApartment ? "Güncelle" : "Kaydet"}
            </button>
          </div>
        </form>
      </Modal>
      {isLoadingApartments && <p className="text-gray-500 text-center py-4">Daireler yükleniyor...</p>}
      {!isLoadingInitialData && !isLoadingApartments && apartments.length === 0 && selectedBlockIdInTableFilter ? <p className="text-gray-500 mt-4">Bu blok için henüz daire eklenmemiş.</p> : null}
      {!isLoadingInitialData && !isLoadingApartments && apartments.length === 0 && !selectedBlockIdInTableFilter && (buildings.length > 0 || blocks.length > 0) ? <p className="text-gray-500 mt-4">Henüz daire eklenmemiş.</p> : null}
      {!isLoadingApartments && apartments.length > 0 ? (
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="th-style">Bina</th>
                <th className="th-style">Blok</th>
                <th className="th-style">Daire No</th>
                <th className="th-style">Kat</th>
                <th className="th-style">Arsa Payı</th>
                <th className="th-style">Kat Maliki</th>
                <th className="th-style">Sakin</th>                
                <th className="th-style">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {apartments.map(apt => (
                <tr key={apt.id}>
                  <td className="td-style">{apt.buildingName || getBuildingName(apt.buildingId)}</td>
                  <td className="td-style">{apt.blockName || getBlockName(apt.blockId)}</td>
                  <td className="td-style font-medium">{apt.apartmentNumber}</td>
                  <td className="td-style">{apt.floor || '-'}</td>
                  <td className="td-style">{apt.arsaPayi}</td>
                  <td className="td-style">{apt.ownerName || getPersonName(apt.ownerId)}</td>
                  <td className="td-style">{apt.residentName || getPersonName(apt.residentId)}</td>
                  <td className="td-style space-x-2">
                    <button
                      onClick={() => openApartmentModalForEdit(apt)}
                      className="icon-button-indigo"
                      title="Düzenle"
                      disabled={isSubmitting}
                    >
                      <Edit3 size={18}/>
                    </button>
                    <button
                      onClick={() => setConfirmDeleteModal({isOpen: true, apartmentId: apt.id})}
                      className="icon-button-red"
                      title="Sil"
                      disabled={isSubmitting}
                    >
                      <Trash2 size={18}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <style jsx>{`
        .th-style { padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }
        .td-style { padding: 0.75rem 1.5rem; white-space: nowrap; font-size: 0.875rem; color: #374151; }
        .label-form-style { display: block; margin-bottom: 0.25rem; font-size: 0.875rem; font-weight: 500; color: #374151; }
        .input-form-style { width: 100%; padding: 0.5rem; border: 1px solid #D1D5DB; border-radius: 0.375rem; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
        .btn-primary-style { padding: 0.5rem 1rem; font-size: 0.875rem; color: white; background-color: #2563EB; border-radius: 0.375rem; } 
        .btn-primary-style:hover { background-color: #1D4ED8; }
        .btn-secondary-style { padding: 0.5rem 1rem; font-size: 0.875rem; color: #374151; background-color: #E5E7EB; border-radius: 0.375rem; } 
        .btn-secondary-style:hover { background-color: #D1D5DB; }
        .icon-button-indigo { color: #4f46e5; } 
        .icon-button-indigo:hover { color: #3730a3; }
        .icon-button-red { color: #dc2626; } 
        .icon-button-red:hover { color: #b91c1c; }
      `}</style>
    </div>
  );
};

export default ApartmentManagementView;
